import React, { FC, MouseEvent, useState } from 'react';
import { ButtonBase, Menu, MenuItem } from '@mui/material';
import { themePalette } from '@theme/muiTheme';
import { SCOPE_TYPE_LABELS, SCOPE_TYPES, ScopeType } from '@utils/domains';
import { DropdownIcon } from '@utils/iconsDefs';
import FilterSubtitle from '@views/Project/components/ModelFiltersPopover/components/FilterSubtitle';
import { filtersPopoverOwnerProps } from '@views/Project/components/ModelFiltersPopover/filtersPopover.store';

export const SCOPE_TYPE_SELECT_LABEL = 'Scope type';

type Props = {
  value: ScopeType;
  onChange: (scopeType: ScopeType) => void;
};

/**
 * The Scope panel's Target / Operationalised selector, the Figma "Inline
 * select": the current type as plain body text with the 20px dropdown caret
 * beside it, opening a 220px menu headed "Scope type" that lists both types
 * with the current one selected.
 *
 * The trigger is a bare `ButtonBase` rather than the house `Button`, whose
 * h4 face, 40px frame and green focus fill make a control of their own; it
 * keeps a thin focus ring so the keyboard can find it. The menu is portaled
 * out of the Filters popover, so its root carries the popover-owner
 * attribute; without it the pointer-down on an item would read as a click
 * away and close the popover before the choice lands. Its paper is the
 * design's 4px-radius, 6px-shadow card set 8px under the trigger, tighter
 * than the theme's menu default; the items themselves are the theme's.
 * Picking the current type only closes the menu.
 */
const ScopeTypeSelect: FC<Props> = ({ value, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const open = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const close = () => setAnchorEl(null);

  const pick = (scopeType: ScopeType) => {
    close();

    if (scopeType !== value) onChange(scopeType);
  };

  return (
    <>
      <ButtonBase
        onClick={open}
        aria-label={SCOPE_TYPE_SELECT_LABEL}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        disableRipple
        sx={{
          gap: '8px',
          height: 24,
          flexShrink: 0,
          typography: 'body1',
          color: 'grey.1000',
          borderRadius: '4px',
          '&.Mui-focusVisible': {
            outline: `1px solid ${themePalette.grey[700]}`,
            outlineOffset: '2px',
          },
        }}
      >
        {SCOPE_TYPE_LABELS[value]}

        <DropdownIcon width={20} height={20} fill={themePalette.grey[900]} />
      </ButtonBase>

      <Menu
        {...filtersPopoverOwnerProps}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 220,
            mt: '8px',
            borderRadius: '4px',
            boxShadow: '0px 4px 6px 0px rgba(0, 0, 0, 0.10)',
          },
        }}
      >
        <FilterSubtitle>{SCOPE_TYPE_SELECT_LABEL}</FilterSubtitle>

        {SCOPE_TYPES.map((scopeType) => (
          <MenuItem
            key={scopeType}
            selected={scopeType === value}
            onClick={() => pick(scopeType)}
          >
            {SCOPE_TYPE_LABELS[scopeType]}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ScopeTypeSelect;
